<template>
  <div class="form-container">
    <teleport to="body">
      <div class="page-overlay" v-if="isLoading">
        <base-loader />
      </div>
    </teleport>

    <h3 ref="stepTitle" class="form-title" tabindex="-1">
      {{ isAgreementStep ? "Условия использования" : "Войти" }}
    </h3>
    <base-error
      class="error_msg"
      :base-error="baseError"
      v-show="baseError.visible"
    />
    <!-- Форма логина -->
    <form class="form-login" @submit.prevent="postLoginData">
      <fieldset :disabled="isLoading" class="form-fieldset">
        <div v-if="!isAgreementStep" class="form-group">
          <base-input
            placeholder="Email или телефон"
            v-model="loginFormData.login"
            required
          />
        </div>

        <div v-if="!isAgreementStep" class="form-group">
          <base-input
            is-password
            placeholder="Пароль"
            v-model="loginFormData.password"
            required
            autocomplete="current-password"
          />
        </div>

        <div v-if="!isAgreementStep" class="form-group options-row">
          <base-checkbox v-model="isForeignComputer">
            <base-tooltip
              text="Включите, если входите не со своего устройства"
              position="bottom"
            >
              <span class="session-option-label">Чужой компьютер</span>
            </base-tooltip>
          </base-checkbox>
        </div>

        <div
          v-if="isAgreementStep"
          class="form-group agreement-step"
          aria-live="polite"
        >
          <p class="agreement-description">
            Для входа ознакомьтесь с актуальной редакцией соглашения и подтвердите
            принятие условий.
          </p>
          <base-checkbox v-model="agreementGiven" align="start">
            Я принимаю условия
            <a
              :href="authStore.requiredAgreement.url"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              Пользовательского соглашения
            </a>
          </base-checkbox>
        </div>

        <base-error-container
          class="error-box"
          :error-container-msg="errorContainerMsg"
          variant="dark"
          v-show="errorContainerMsg.visible"
        />

        <base-primary-button
          class="form-btn"
          type="submit"
          :disabled="isLoading || (isAgreementStep && !agreementGiven)"
        >
          {{ isAgreementStep ? "Принять и войти" : "Войти" }}
        </base-primary-button>
      </fieldset>
    </form>

    <button
      v-if="isAgreementStep"
      class="forgot-btn"
      :disabled="isLoading"
      @click="backToLogin"
    >
      Вернуться к входу
    </button>
    <button
      v-else
      class="forgot-btn"
      :disabled="isLoading"
      @click="goToRecovery"
    >
      Забыли пароль?
    </button>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/authStore";

export default {
  name: "LoginForm",

  props: {
    baseErrorText: {
      type: String,
      required: false,
    },
    onSuccessfulLogin: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      agreementGiven: false,
      loginFormData: {
        login: "",
        password: "",
        remember: true,
      },

      baseError: { visible: false, text: "Возникла ошибка" },
      errorContainerMsg: { visible: false, text: "" },
    };
  },

  computed: {
    authStore() {
      return useAuthStore();
    },

    isAgreementStep() {
      return Boolean(this.authStore.requiredAgreement);
    },

    isLoading() {
      return this.authStore.isSubmitting;
    },

    isForeignComputer: {
      get() {
        return !this.loginFormData.remember;
      },

      set(value) {
        this.loginFormData.remember = !value;
      },
    },
  },

  watch: {
    async isAgreementStep() {
      this.agreementGiven = false;
      await this.$nextTick();
      this.$refs.stepTitle?.focus();
    },
  },

  methods: {
    async postLoginData() {
      this.errorContainerMsg.visible = false;
      if (this.isLoading || (this.isAgreementStep && !this.agreementGiven)) return;

      try {
        const loggedIn = this.isAgreementStep
          ? await this.authStore.confirmAgreement(window.location.href)
          : await this.authStore.loginWithCredentials(this.loginFormData);
        if (!loggedIn) {
          this.loginFormData.password = "";
          this.agreementGiven = false;
          return;
        }

        if (typeof this.onSuccessfulLogin === "function") {
          await this.onSuccessfulLogin();
        } else {
          const returnUrl = this.authStore.returnUrl;
          if (returnUrl) {
            await this.$router.push(returnUrl);
          } else {
            await this.$router.push({ name: "Home" });
          }
        }
      } catch (e) {
        this.errorContainerMsg.visible = true;
        if (e.response) {
          if (e.response.status === 401) {
            this.errorContainerMsg.text =
              "Пользователь с такими данными не найден";
          } else {
            this.errorContainerMsg.text =
              e.response.data?.errors || "Произошла ошибка";
          }
        } else {
          this.errorContainerMsg.text =
            "Не удалось связаться с сервером. Проверьте интернет и попробуйте ещё раз.";
        }
      }
    },

    backToLogin() {
      this.authStore.resetAgreementStep();
      this.errorContainerMsg.visible = false;
      this.loginFormData.password = "";
    },

    goToRecovery() {
      this.$router.push({ name: "AccountRecovery" });
    },
  },

  created() {
    this.baseError.text = this.baseErrorText;
    if (this.baseErrorText !== "Возникла ошибка") {
      this.baseError.visible = true;
    }
  },
};
</script>

<style scoped>
.page-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.form-container {
  margin-top: 15vh;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 50px;
  background: var(--color-dark);
  border-radius: 20px;
}

.form-title {
  max-width: 290px;
  text-align: center;
  margin-bottom: var(--base-out-indent);
  font-size: 26px;
  font-weight: bold;
  color: var(--color-light);
}

.error_msg {
  width: 290px;
}

.form-login {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 290px;
}

.form-fieldset {
  display: contents;
  border: none;
  padding: 0;
  margin: 0;
}

.form-group {
  position: relative;
  width: 290px;
}

.options-row {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-start;
  color: var(--color-light);
}

.agreement-step {
  margin-bottom: 20px;
  color: var(--color-light);
}

.agreement-description {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.5;
}

.agreement-step a {
  color: inherit;
  text-decoration: underline;
}

.session-option-label {
  cursor: help;
}

.error-box {
  margin-bottom: var(--base-in-indent);
}

.form-btn {
  width: 290px;
}

.forgot-btn {
  width: max-content;
  height: max-content;
  margin-top: 20px;
  padding: 0;
  background-color: transparent;
  color: var(--color-light);
  font-size: 14px;
  text-decoration: underline;
  border: none;
  cursor: pointer;
}

.forgot-btn:hover {
  text-decoration: none;
  background-color: transparent;
}

.forgot-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  text-decoration: none;
}

@media screen and (max-width: 767px) {
  .form-container {
    padding-left: 0;
    padding-right: 0;
    border-radius: 0;
    background-color: transparent;
  }

  .form-title {
    margin-bottom: 20px;
  }
}
</style>
